import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AutocompleteSearch } from "@/components/pendingShipments/autocompleteSearch";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { CountryCode } from "libphonenumber-js";
import { useEffect, useMemo } from "react";
import { CustomCotationsOtherFormData } from "@/types/forms/home/customCotationsOtherForm.form";
import clsx from "clsx";
import { ControlledUnifiedInput } from "../ui/customs/controlled-unified-input";
import { ControlledCustomsSelect } from "../ui/customs/controlled-customs-select";
import { GetCustomsV1Response } from "@/types/api/api-types";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";

export const InfoDeliveryOtherType = ({ customs }: { customs: GetCustomsV1Response }) => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationsOtherFormData>();
  const singleDelivery = methods.watch("deliveryInfo.singleDelivery");

  const selectedCustom = useWatch({
    control: methods.control,
    name: "deliveryInfo.customId",
  });

  const currentCountryCode = useMemo(() => {
    const countryCode = customs?.find((custom) => custom.id === selectedCustom)?.flagCode;
    if (countryCode) {
      methods.setValue("deliveryInfo.countryCode", countryCode);
    }
    return countryCode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customs, selectedCustom]);

  const countryCode = currentCountryCode ?? "";
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handleDeliveryCountryChange = (customId: string) => {
    methods.setValue("deliveryInfo.deliveryStreet", "");
    methods.setValue("deliveryInfo.deliveryPostalCode", "");
    methods.setValue("deliveryInfo.deliveryCity", "");
    methods.setValue("deliveryInfo.stateOrProvinceCode", "");
    methods.setValue("deliveryInfo.deliveryPhone", "");
    methods.setValue("deliveryInfo.countryCode", "");

    methods.setValue("deliveryInfo.customId", customId);
    const customCountryCode = customs.find((custom) => custom.id === customId)?.flagCode;
    if (customCountryCode) {
      methods.setValue("deliveryInfo.countryCode", customCountryCode);
    }
  };

  useEffect(() => {
    if (singleDelivery) {
      methods.setValue("deliveryInfo.otherNumberOfExpedition", 0);
    } else {
      methods.setValue("deliveryInfo.countryCode", "");
      methods.setValue("deliveryInfo.deliveryStreet", "");
      methods.setValue("deliveryInfo.deliveryPostalCode", "");
      methods.setValue("deliveryInfo.deliveryCity", "");
      methods.setValue("deliveryInfo.stateOrProvinceCode", "");
      methods.setValue("deliveryInfo.deliveryPhone", "");
      methods.setValue("deliveryInfo.deliveryCompany", "");
      methods.setValue("deliveryInfo.deliveryFirstName", "");
      methods.setValue("deliveryInfo.deliveryLastName", "");
      methods.setValue("deliveryInfo.deliveryEmail", "");
      methods.setValue("deliveryInfo.deliveryAdditionalInfo", "");
      methods.setValue("deliveryInfo.deliveryAdditionalInfo2", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleDelivery]);

  const defaultCountry: CountryCode = (countryCode || "FR") as CountryCode;

  const handlePlaceClicked = (place: google.maps.places.PlaceResult | null) => {
    const parsed = parseGooglePlaceAddress(place, countryCode);
    if (!parsed) return;

    methods.setValue("deliveryInfo.deliveryStreet", parsed.street);
    methods.setValue("deliveryInfo.deliveryPostalCode", parsed.postalCode);
    methods.setValue("deliveryInfo.deliveryCity", parsed.city);

    if (showStateOrProvince) {
      methods.setValue("deliveryInfo.stateOrProvinceCode", parsed.stateOrProvinceCode);
    }

    // Only validate filled fields: in onSubmit mode, an error raised here on an empty
    // field would not be cleared when the user types until the form is submitted.
    const filledFields = (
      [
        ["deliveryInfo.deliveryStreet", parsed.street],
        ["deliveryInfo.deliveryPostalCode", parsed.postalCode],
        ["deliveryInfo.deliveryCity", parsed.city],
        ...(showStateOrProvince
          ? ([["deliveryInfo.stateOrProvinceCode", parsed.stateOrProvinceCode]] as const)
          : []),
      ] as const
    )
      .filter(([, value]) => value)
      .map(([name]) => name);
    methods.trigger(filledFields);
  };

  const handleStreetInput = (e: string) => {
    methods.setValue("deliveryInfo.deliveryStreet", e);
    methods.trigger(["deliveryInfo.deliveryStreet"]);
  };

  return (
    <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <h2 className="text-brand700 text-xl font-semibold">{t("deliveryInfoOther.title")}</h2>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-row justify-center gap-2">
            <button
              type="button"
              onClick={() => {
                methods.setValue("deliveryInfo.singleDelivery", true);
                methods.trigger("deliveryInfo.singleDelivery");
              }}
              className={`rounded-lg border px-4 py-2 transition-colors ${
                singleDelivery
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("deliveryInfoOther.singleDelivery")}
            </button>
            <button
              type="button"
              onClick={() => {
                methods.setValue("deliveryInfo.singleDelivery", false);
                methods.trigger("deliveryInfo.singleDelivery");
              }}
              className={`rounded-lg border px-4 py-2 transition-colors ${
                !singleDelivery
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("deliveryInfoOther.multipleDeliveries")}
            </button>
          </div>
        </div>
        <div className={clsx("flex w-full flex-col gap-2", !singleDelivery && "hidden")}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryLastName"
              label={t("deliveryInfoOther.lastNameLabel")}
              wrapperClassName="w-full"
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryFirstName"
              label={t("deliveryInfoOther.firstNameLabel")}
              wrapperClassName="w-full"
            />
          </div>
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.deliveryCompany"
            label={t("deliveryInfoOther.companyLabel")}
            wrapperClassName="w-full"
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.deliveryEmail"
            label={t("deliveryInfoOther.emailLabel")}
            wrapperClassName="w-full"
          />
          <ControlledCustomsSelect
            customs={customs || []}
            control={methods.control}
            name="deliveryInfo.customId"
            label={t("deliveryInfoOther.destinationLabel")}
            setValue={(customId: string) => handleDeliveryCountryChange(customId)}
          />
          <AutocompleteSearch
            placeholder={t("deliveryInfoOther.streetPlaceholder")}
            onPlaceClicked={handlePlaceClicked}
            control={methods.control}
            handleStreetInput={handleStreetInput}
            formValueOverride="deliveryInfo.deliveryStreet"
            disabled={!countryCode}
            countryCode={countryCode ? [countryCode] : []}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryAdditionalInfo"
              label={t("deliveryInfoOther.additionalInfoLabel")}
              wrapperClassName="w-full"
              disabled={!countryCode}
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryAdditionalInfo2"
              label={t("deliveryInfoOther.additionalInfo2Label")}
              wrapperClassName="w-full"
              disabled={!countryCode}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {showPostalCode && (
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.deliveryPostalCode"
                label={t("deliveryInfoOther.postalCodeLabel")}
                wrapperClassName="flex-1"
                disabled={!countryCode}
              />
            )}
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryCity"
              label={t("deliveryInfoOther.cityLabel")}
              wrapperClassName="flex-1"
              disabled={!countryCode}
            />
            {showStateOrProvince && (
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.stateOrProvinceCode"
                label={t("deliveryInfoOther.stateOrProvinceLabel")}
                wrapperClassName="flex-1"
                disabled={!countryCode}
              />
            )}
          </div>
          <StyledPhoneInput
            control={methods.control}
            name="deliveryInfo.deliveryPhone"
            title={t("deliveryInfoOther.phoneLabel")}
            className="w-full"
            disabled={!countryCode}
            defaultCountry={defaultCountry}
          />
        </div>
        <ControlledUnifiedInput
          control={methods.control}
          name="deliveryInfo.otherNumberOfExpedition"
          label={t("deliveryInfoOther.deliveryPointsLabel")}
          inputType="integer"
          disabled={singleDelivery}
          wrapperClassName={clsx("w-full", singleDelivery && "hidden")}
        />
      </div>
    </div>
  );
};
